import { SearchType, UserType } from '@case-search/domain';
import {
  Card,
  Button,
  Dropdown,
  type DropdownOption,
  TextInput,
  IconButton,
  ButtonSize,
  ButtonType,
  IconSize,
  OverlayMode,
  IconButtonSize,
  Search,
  Filter,
} from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

import * as styles from './CaseSearchPanel.css';

export interface CaseSearchPanelProps {
  isLoading?: boolean;
  onSearch: (searchType: SearchType, searchValue: string, userType?: UserType) => void;
}

export function CaseSearchPanel({ isLoading = false, onSearch }: CaseSearchPanelProps) {
  const [searchType, setSearchType] = useState<SearchType>(SearchType.USER_ID);
  const [userType, setUserType] = useState<UserType | undefined>(undefined);
  const [searchValue, setSearchValue] = useState('');
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

  const handleSearch = useCallback(() => {
    onSearch(searchType, searchValue, userType);
  }, [searchType, searchValue, userType, onSearch]);

  const handleToggleFiltersPanel = useCallback(() => {
    setIsFiltersPanelOpen(!isFiltersPanelOpen);
  }, [isFiltersPanelOpen]);

  const handleSearchTypeChange = useCallback((value: string) => {
    setSearchType(value as SearchType);
    // Reset userType when search type changes
    if (value !== SearchType.USER_ID) {
      setUserType(undefined);
    }
  }, []);

  const searchTypeOptions: DropdownOption[] = [
    { id: SearchType.SUBJECT_ID, label: SearchType.SUBJECT_ID },
    { id: SearchType.USER_ID, label: SearchType.USER_ID },
    { id: SearchType.CASE_ID, label: SearchType.CASE_ID },
  ];

  const userTypeOptions: DropdownOption[] = [
    { id: UserType.XBOX, label: UserType.XBOX },
    // Add more user types as needed
  ];

  // Dynamic placeholder based on search type
  const getSearchPlaceholder = () => {
    switch (searchType) {
      case SearchType.USER_ID:
        return 'Enter exact User ID value';
      case SearchType.SUBJECT_ID:
        return 'Enter exact Subject ID value';
      case SearchType.CASE_ID:
        return 'Enter exact Case ID value';
      default:
        return 'Enter search value';
    }
  };

  // Show User Type dropdown only when Search Type is User ID
  const showUserTypeDropdown = searchType === SearchType.USER_ID;

  return (
    <Card className={styles.searchCard}>
      <div className={styles.mainFilterBar}>
        {/* Left: Full-width search input (primary action) */}
        <div className={styles.filterBarLeft}>
          <TextInput
            aria-label="Search value"
            disabled={isLoading}
            placeholder={getSearchPlaceholder()}
            prefixIcon={Search}
            value={searchValue}
            onChange={setSearchValue}
          />
        </div>

        {/* Right: Controls group (secondary filters and actions) */}
        <div className={styles.controlGroup} data-testid="control-group">
          {/* Search Type Dropdown */}
          <div>
            <span className={styles.visuallyHidden}>Search Type</span>
            <Dropdown
              aria-label="Search Type"
              disabled={isLoading}
              options={searchTypeOptions}
              placeholder="Search Type"
              selectedItem={searchType}
              size="medium"
              onSelectionChange={handleSearchTypeChange}
            />
          </div>

          {/* User Type Dropdown - conditionally visible */}
          {showUserTypeDropdown && (
            <div>
              <span className={styles.visuallyHidden}>User Type</span>
              <Dropdown
                aria-label="User Type"
                disabled={isLoading}
                options={userTypeOptions}
                placeholder="User Type"
                selectedItem={userType}
                size="medium"
                onSelectionChange={(value: string) => setUserType(value as UserType)}
              />
            </div>
          )}

          {/* Search Button */}
          <Button buttonSize={ButtonSize.Default} buttonType={ButtonType.Brand} disabled={isLoading} label="SEARCH" onClick={handleSearch} />

          {/* Filter Toggle Button */}
          <IconButton
            aria-label={isFiltersPanelOpen ? 'Hide filters' : 'Show filters'}
            buttonSize={IconButtonSize.Medium}
            buttonType={ButtonType.Primary}
            className={styles.filterButton}
            data-active={isFiltersPanelOpen}
            disabled={isLoading}
            icon={Filter}
            iconSize={IconSize.XSmall}
            overlayMode={OverlayMode.Solid}
            title={isFiltersPanelOpen ? 'Hide filters' : 'Show filters'}
            onClick={handleToggleFiltersPanel}
          />
        </div>
      </div>
    </Card>
  );
}
